import { locationManager } from '@telegram-apps/sdk-react';
import { getLaunchParams } from './telegram';

export type LocationErrorReason = 'denied' | 'unavailable' | 'timeout' | 'desktop';

export class LocationError extends Error {
  constructor(readonly reason: LocationErrorReason) {
    super(reason);
  }
}

/** Telegram'ning kompyuter klientlari WebView'ga geolokatsiya bermaydi. */
const DESKTOP_PLATFORMS = new Set(['tdesktop', 'macos', 'weba', 'webk', 'web']);

export const isDesktopTelegram = (): boolean => {
  try {
    return DESKTOP_PLATFORMS.has(getLaunchParams().tgWebAppPlatform);
  } catch {
    return false;
  }
};

export const canOpenLocationSettings = () => locationManager.openSettings.isAvailable();
export const openLocationSettings = () => locationManager.openSettings.ifAvailable();

async function viaTelegram(): Promise<{ lat: number; lng: number }> {
  if (!locationManager.isMounted()) await locationManager.mount();
  if (!locationManager.isAvailable()) throw new LocationError('unavailable');
  // Avval rad etilgan bo'lsa Telegram qayta so'ramaydi — sozlamalarga yo'naltiramiz.
  if (locationManager.isAccessRequested() && !locationManager.isAccessGranted()) {
    throw new LocationError('denied');
  }
  try {
    const loc = (await locationManager.requestLocation()) as {
      latitude: number;
      longitude: number;
    };
    return { lat: loc.latitude, lng: loc.longitude };
  } catch {
    throw new LocationError(locationManager.isAccessGranted() ? 'unavailable' : 'denied');
  }
}

function viaBrowser(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new LocationError('unavailable'));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      (e) =>
        reject(
          new LocationError(
            e.code === e.PERMISSION_DENIED
              ? 'denied'
              : e.code === e.TIMEOUT
                ? 'timeout'
                : 'unavailable',
          ),
        ),
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 },
    );
  });
}

/**
 * Joriy joylashuv: Telegram LocationManager (Bot API 8.0+, mobil) → brauzer geolokatsiyasi.
 * Faqat foydalanuvchi bosganda chaqirilsin (Telegram talabi).
 */
export async function getCurrentLocation(): Promise<{ lat: number; lng: number }> {
  if (locationManager.mount.isAvailable()) {
    try {
      return await viaTelegram();
    } catch (e) {
      if (e instanceof LocationError && e.reason === 'denied') throw e;
      // Telegram API ishlamadi — brauzer usulini sinaymiz.
    }
  }
  try {
    return await viaBrowser();
  } catch (e) {
    if (isDesktopTelegram()) throw new LocationError('desktop');
    throw e instanceof LocationError ? e : new LocationError('unavailable');
  }
}
